import makeApi, { URLS } from "Utils/apiURL";
import scheduleDataStore from "Stores/ScheduleData";
import userDetail from "Stores/userDetail";
import { toasterState } from "Components/common/toaster";
import { getGlobalTimeZones } from "Utils/commonAPI";

const WEEKDAYS = ["1", "2", "3", "4", "5"];
const WEEKENDS = ["6", "7"];
const ALL_DAYS = ["1", "2", "3", "4", "5", "6", "7"];

const buildTimeRange = (days = [], fromTime = "09:00", toTime = "17:00") => {
  const timeRange = {};
  days.forEach(day => {
    timeRange[day] = [{ fromTime, toTime }];
  });
  return timeRange;
};

// timezoneCandidates: possible ids in the backend timezone list (first match wins);
// timezoneName is only a fallback label when no list entry matches.
export const PRESET_SCHEDULES = [
  {
    key: "usEasternWeekdays",
    name: "US Eastern Weekdays 9am-5pm",
    hint: "Mon-Fri, New York",
    timezoneCandidates: ["America/New_York", "US/Eastern"],
    timezoneName: "(GMT-05:00) Eastern Time (US & Canada)",
    timerange: buildTimeRange(WEEKDAYS)
  },
  {
    key: "usPacificWeekdays",
    name: "US Pacific Weekdays 9am-5pm",
    hint: "Mon-Fri, Los Angeles",
    timezoneCandidates: ["America/Los_Angeles", "US/Pacific"],
    timezoneName: "(GMT-08:00) Pacific Time (US & Canada)",
    timerange: buildTimeRange(WEEKDAYS)
  },
  {
    key: "usCentralWeekdays",
    name: "US Central Weekdays 9am-5pm",
    hint: "Mon-Fri, Chicago",
    timezoneCandidates: ["America/Chicago", "US/Central"],
    timezoneName: "(GMT-06:00) Central Time (US & Canada)",
    timerange: buildTimeRange(WEEKDAYS)
  },
  {
    key: "ukWeekdays",
    name: "UK Weekdays 9am-5pm",
    hint: "Mon-Fri, London",
    timezoneCandidates: ["Europe/London"],
    timezoneName: "(GMT+00:00) London",
    timerange: buildTimeRange(WEEKDAYS)
  },
  {
    key: "centralEuropeWeekdays",
    name: "Central Europe Weekdays 9am-5pm",
    hint: "Mon-Fri, Berlin",
    timezoneCandidates: ["Europe/Berlin", "Europe/Paris", "Europe/Amsterdam"],
    timezoneName: "(GMT+01:00) Berlin",
    timerange: buildTimeRange(WEEKDAYS)
  },
  {
    key: "indiaWorkingHours",
    name: "India Working Hours 9:30am-6:30pm",
    hint: "Mon-Fri, IST",
    timezoneCandidates: ["Asia/Kolkata", "Asia/Calcutta"],
    timezoneName: "(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi",
    timerange: buildTimeRange(WEEKDAYS, "09:30", "18:30")
  },
  {
    key: "usEasternWeekends",
    name: "US Eastern Weekends 10am-4pm",
    hint: "Sat-Sun, New York",
    timezoneCandidates: ["America/New_York", "US/Eastern"],
    timezoneName: "(GMT-05:00) Eastern Time (US & Canada)",
    timerange: buildTimeRange(WEEKENDS, "10:00", "16:00")
  },
  {
    key: "anytime",
    name: "Anytime 24/7",
    hint: "All days, all hours",
    timezoneCandidates: ["America/New_York", "US/Eastern"],
    timezoneName: "(GMT-05:00) Eastern Time (US & Canada)",
    timerange: buildTimeRange(ALL_DAYS, "00:00", "23:59")
  }
];

const findTimezoneEntry = (preset = {}) => {
  const zones = userDetail?.globalTimeZone || [];
  return zones.find(zone =>
    preset?.timezoneCandidates?.includes(zone?.name)
  );
};

const findScheduleByName = (name = "") => {
  return scheduleDataStore?.scheduleData?.data?.find(
    item => item?.name === name
  );
};

// Creates a schedule from a preset via the existing timewindow API and returns
// the created (or already existing) schedule object; null on failure.
export const createScheduleFromPreset = async (preset = {}) => {
  const existing = findScheduleByName(preset?.name);
  if (existing?.id) {
    return existing;
  }

  if (!userDetail?.globalTimeZone?.length) {
    await getGlobalTimeZones();
  }
  const timezoneEntry = findTimezoneEntry(preset);

  try {
    const config = {
      url: URLS.saveScheduleTimeWindow,
      method: "POST",
      data: {
        name: preset?.name,
        timezone: timezoneEntry?.name || preset?.timezoneCandidates?.[0],
        timezoneName: timezoneEntry?.displayName || preset?.timezoneName,
        timerange: preset?.timerange
      }
    };
    const response = await makeApi(config);
    if (response?.status === 200) {
      scheduleDataStore.setScheduleDataLoading(true);
      await scheduleDataStore.getScheduleData();
      const created = findScheduleByName(preset?.name);
      if (created?.id) {
        return created;
      }
    }
  } catch (e) {
    console.error("Failed to create schedule from preset", e);
  }
  toasterState.setToastMsg(
    "Failed to create the schedule. Please try again.",
    "fail"
  );
  return null;
};
